// React / 旧 Vue 共通。認証セッションとは独立した分析用状態だけを保存する。
export const SESSION_STORAGE_KEY = 'luna:usage-session:v1';
const LOCK = 'luna:usage-session';
const IDLE_MS = 30 * 60 * 1000;
const RETRY_MS = 5 * 60 * 1000;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const USER_ID = /^[1-9][0-9]{0,18}$/;
const ENDPOINT = '/api/v2/events/user-session-started';
const INVALID_STATE = Symbol('invalid session state');

export function isSessionTrackingPath(path) {
    if (/^\/(?:admin|event\/admin|suspended|setting\/logout)(?:\/|$)/.test(path)) return false;
    if (/^\/signup\/(?:section\/|referral-source$|completed$)/.test(path)) return true;
    if (path === '/plan') return false; // 公開 LP の閲覧だけでは開始しない。
    return /^\/(?:home|search|trial|plan|user|x\/user|profile|good|matching|congratulation|premium|setting|footprint|event|shop|card|age-verification|info)(?:\/|$)/.test(path);
}

export function createSessionTracker(win) {
    let active = false;
    let path = '';
    let generation = 0;
    let owner = null;
    let userId = null;
    let pendingActivity = null;
    let refresh = false;
    let running = false;
    let retryTimer = null;
    let activityTimer = null;
    let disposed = false;

    const visible = () => win.document.visibilityState === 'visible';
    const supported = () => win.isSecureContext && win.navigator.locks?.request && win.crypto?.randomUUID;
    const clearTimers = () => {
        win.clearTimeout(retryTimer);
        win.clearTimeout(activityTimer);
        retryTimer = activityTimer = null;
    };
    const pause = () => {
        generation++;
        active = false;
        owner = userId = null;
        pendingActivity = null;
        refresh = false;
        clearTimers();
    };
    const read = (raw = win.localStorage.getItem(SESSION_STORAGE_KEY)) => {
        if (raw === null) return null;
        let state;
        try { state = JSON.parse(raw); } catch { return INVALID_STATE; }
        if (!state || typeof state !== 'object' || Array.isArray(state)) return INVALID_STATE;
        // 新しいアプリが書いた形式を古いタブから上書きしない。
        if (Number.isInteger(state.version) && state.version > 1) throw new Error('unsupported state version');
        if (state.version !== 1 || !UUID.test(state.owner)) return INVALID_STATE;
        if (state.userId === null) return state; // 終了を他タブにも伝える tombstone。
        if (!USER_ID.test(state.userId) || !UUID.test(state.sessionId) ||
            !['startedAt', 'lastActivityAt', 'nextAttemptAt'].every(key => Number.isFinite(state[key])) ||
            typeof state.notified !== 'boolean') return INVALID_STATE;
        return state;
    };
    const write = state => {
        const raw = JSON.stringify(state);
        win.localStorage.setItem(SESSION_STORAGE_KEY, raw);
        return raw;
    };
    const empty = () => ({ version: 1, owner: win.crypto.randomUUID(), userId: null });
    const locked = async callback => {
        const abort = new win.AbortController();
        const timer = win.setTimeout(() => abort.abort(), 12000);
        try {
            return await win.navigator.locks.request(LOCK, { signal: abort.signal }, callback);
        } finally {
            win.clearTimeout(timer);
        }
    };
    const request = async (url, options = {}) => {
        const abort = new win.AbortController();
        const timer = win.setTimeout(() => abort.abort(), 5000);
        try {
            return await win.fetch(url, {
                credentials: 'same-origin', cache: 'no-store', ...options, signal: abort.signal,
                headers: { Accept: 'application/json', ...options.headers },
            });
        } finally {
            win.clearTimeout(timer);
        }
    };
    const context = async () => {
        const response = await request(`${ENDPOINT}/context`);
        if ([401, 403].includes(response.status)) return null;
        if (!response.ok) throw new Error('context unavailable');
        const data = await response.json();
        if (data.enabled === false) return null;
        if (data.enabled !== true || typeof data.user_id !== 'string' || !USER_ID.test(data.user_id)) {
            throw new Error('invalid context');
        }
        return data.user_id;
    };
    const csrf = () => {
        const cookie = win.document.cookie.split(';').map(value => value.trim())
            .find(value => value.startsWith('XSRF-TOKEN='));
        if (cookie) return { 'X-XSRF-TOKEN': decodeURIComponent(cookie.slice(11)) };
        const token = win.document.querySelector('meta[name="csrf-token"]')?.content;
        return token ? { 'X-CSRF-TOKEN': token } : null;
    };
    const scheduleRetry = state => {
        win.clearTimeout(retryTimer);
        const now = Date.now();
        if (state.notified || state.nextAttemptAt >= state.startedAt + RETRY_MS || now >= state.startedAt + RETRY_MS) return;
        retryTimer = win.setTimeout(() => queue(false, false), Math.max(0, state.nextAttemptAt - now));
    };

    async function process(observedAt, needsContext, current) {
        await locked(async () => {
            if (!active || current !== generation || !visible()) return;
            let snapshot = win.localStorage.getItem(SESSION_STORAGE_KEY);
            let state = read(snapshot);
            // ログアウトはロックを待たず同期で終了状態を書く。通信待ちの後は
            // storage イベントの配送を待たず照合し、古い状態を再保存しない。
            const stillCurrent = () => {
                if (win.localStorage.getItem(SESSION_STORAGE_KEY) !== snapshot) {
                    if (current === generation) pause();
                    return false;
                }
                return true;
            };
            // 別タブで終了・本人切替済みなら、古い画面から状態を取り戻さない。
            if (owner && state !== INVALID_STATE && (!state || state.owner !== owner)) { pause(); return; }
            const now = Date.now();
            // ロック待ち中に時計が戻った場合も、未来の操作時刻を新しい状態へ持ち込まない。
            const activityAt = observedAt === null ? null : Math.min(observedAt, now);
            if (state === INVALID_STATE || state?.lastActivityAt > now) {
                if (activityAt === null) return; // 復旧も画面表示・実操作を起点にする。
                // 本人確認後に新しい世代・IDで置き換える。壊れた通知や期限は引き継がない。
                state = null;
            }
            let identity = userId;
            const newSession = !state?.userId || (activityAt !== null && activityAt - state.lastActivityAt >= IDLE_MS);
            const needsSend = state?.userId && !state.notified && now >= state.nextAttemptAt && now < state.startedAt + RETRY_MS;
            if (needsContext || newSession || needsSend) {
                identity = await context();
                if (!active || current !== generation || !visible() || !stillCurrent()) return;
                if (!identity || (userId && identity !== userId)) {
                    write(empty());
                    pause();
                    return;
                }
            }
            if (!identity) return;
            // display() だけが新しい本人を採用できる。通常操作・再試行は既存の本人と照合する。
            if (!state?.userId || state.userId !== identity) {
                state = { ...empty(), userId: identity };
            }
            if (!state.sessionId || newSession) {
                if (activityAt === null) return; // タイマーだけでセッションを開始しない。
                state = { ...state, sessionId: win.crypto.randomUUID(), startedAt: activityAt,
                    lastActivityAt: activityAt, nextAttemptAt: now, notified: false };
            } else if (activityAt !== null) {
                state.lastActivityAt = Math.max(state.lastActivityAt, activityAt);
            }
            owner = state.owner;
            userId = identity;
            snapshot = write(state);
            if (!state.notified && now >= state.nextAttemptAt && now < state.startedAt + RETRY_MS) {
                const headers = csrf();
                if (!headers) { pause(); return; }
                // 送信中のタブが閉じても、他タブは同じ ID・期限で再通知できる。
                state.nextAttemptAt = now + 10000;
                snapshot = write(state);
                let response;
                try {
                    response = await request(ENDPOINT, {
                        method: 'POST', headers: { 'Content-Type': 'application/json', ...headers },
                        body: JSON.stringify({ user_id: identity, session_id: state.sessionId }),
                    });
                } catch {
                    // 通信失敗は同じ ID で期限内に再通知する。
                }
                if (!stillCurrent()) return;
                if (response?.status === 204) state.notified = true;
                else if (response && ![429, 503].includes(response.status) && response.status < 500) {
                    // 本人不一致・認証切れなどは再試行しない。
                    write(empty());
                    pause();
                    return;
                }
                write(state);
            }
            if (current === generation && active) scheduleRetry(state);
        });
    }

    async function drain() {
        if (running || !active || !visible()) return;
        running = true;
        const current = generation;
        const observedAt = pendingActivity;
        const needsContext = refresh;
        pendingActivity = null;
        refresh = false;
        try {
            await process(observedAt, needsContext, current);
        } catch {
            // Web Locks / 保存領域 / 本人確認が使えない場合は安全に見送る。
            if (current === generation) pause();
        } finally {
            running = false;
            if (active && (pendingActivity !== null || refresh)) void drain();
        }
    }
    function queue(isActivity = true, needsContext = false) {
        if (!active || !visible()) return;
        if (isActivity) pendingActivity = Date.now();
        refresh ||= needsContext;
        void drain();
    }
    const display = nextPath => {
        pause();
        path = nextPath;
        if (disposed || !supported() || !isSessionTrackingPath(path)) return;
        active = true;
        queue(true, true);
    };
    const end = () => {
        pause();
        // 遷移でページが破棄されても終了状態を残す。通信中の別タブのロックは待たない。
        try { write(empty()); } catch {
            // UUID 生成・書き込みが使えない場合も、既存状態の削除を試みる。
            try { win.localStorage.removeItem(SESSION_STORAGE_KEY); } catch { /* ログアウトを妨げない。 */ }
        }
    };
    const onActivity = () => {
        if (!active || !visible()) return;
        // scroll / input をまとめる。時刻は処理時刻ではなく最後の実操作時刻を保持する。
        pendingActivity = Date.now();
        if (activityTimer === null) {
            // 区間の最初の操作を即時に反映し、境界直前の操作を後続操作で消さない。
            void drain();
            activityTimer = win.setTimeout(() => {
                activityTimer = null;
                if (pendingActivity !== null) void drain();
            }, 250);
        }
    };
    const onVisibility = () => { if (visible()) queue(true, true); };
    const onPageShow = event => { if (event.persisted) display(path); };
    const onStorage = event => {
        if (event.key !== SESSION_STORAGE_KEY && event.key !== null) return;
        try {
            const state = read();
            // 破損の通知だけでは開始せず、次の実操作でロック内から復旧する。
            if (owner && state !== INVALID_STATE && state?.owner !== owner) pause();
        } catch { pause(); }
    };
    ['click', 'input', 'scroll'].forEach(type => win.document.addEventListener(type, onActivity, { capture: true, passive: true }));
    win.document.addEventListener('visibilitychange', onVisibility);
    win.addEventListener('pageshow', onPageShow);
    win.addEventListener('pagehide', pause);
    win.addEventListener('storage', onStorage);
    return {
        display, pause, end,
        dispose() {
            disposed = true;
            pause();
            ['click', 'input', 'scroll'].forEach(type => win.document.removeEventListener(type, onActivity, true));
            win.document.removeEventListener('visibilitychange', onVisibility);
            win.removeEventListener('pageshow', onPageShow);
            win.removeEventListener('pagehide', pause);
            win.removeEventListener('storage', onStorage);
        },
    };
}

let tracker;
export function getSessionTracker() {
    if (!tracker && typeof window !== 'undefined') tracker = createSessionTracker(window);
    return tracker;
}
